import type maplibregl from 'maplibre-gl';

export interface HaloPulseOptions {
    innerLayerId: string;
    outerLayerId: string;
    /** Radius every ring is born at before expanding (kept very small — a tight dot at the pin). */
    minRadius: number;
    /** Radius each ring expands out to as it fades. */
    maxRadius: number;
    /** Opacity a ring is born at; fades to 0 as it expands. */
    peakOpacity: number;
    /** Lifetime of one ring — born tiny, grows to maxRadius, fades to nothing (ms). */
    cycleMs: number;
    /** How long a new ring overlaps the previous fading one (ms). Smaller = less overlap. */
    overlapMs: number;
    /** Number of rings to emit before settling to the static halos. */
    pulses: number;
    /** Duration of the settle tween from the last rings into the static halos (ms). */
    settleMs: number;
    /** Resting radius / opacity the inner halo settles to once pulsing ends. */
    innerSettleRadius: number;
    innerSettleOpacity: number;
    /** Resting radius / opacity the outer halo settles to once pulsing ends. */
    outerSettleRadius: number;
    outerSettleOpacity: number;
    /**
     * Maps a frame's radius to the paint value written — e.g. a zoom-interpolated
     * expression so the halo tracks a marker whose icon-size scales with zoom (the van).
     * Defaults to the plain number.
     */
    toRadiusPaintValue?: (radius: number) => number | maplibregl.ExpressionSpecification;
}

export interface HaloPulse {
    /** Start the pulse: a few overlapping rings, then settle to the static halos. */
    play(): void;
    /** Cancel the animation. */
    stop(): void;
}

// Quadratic ease-out — fast start, gentle settle. Shared shape with the semantic zoom pulse.
const easeOut = (x: number): number => 1 - (1 - x) * (1 - x);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

interface RingState {
    radius: number;
    opacity: number;
}

/**
 * Overlapping "radar" selection pulse for the selected-POI halos — the canvas-layer echo of the
 * semantic zoom control's pulse. Rings are born tiny at the pin, grow to maxRadius and fade to
 * nothing over `cycleMs`; a fresh ring is born `overlapMs` before the previous one finishes, so
 * consecutive rings overlap a little as the old one fades and the new one grows. The two halo
 * layers render alternate rings (at most two are ever alive at once). After `pulses` rings the
 * two layers tween over `settleMs` into the static resting halos (the two settled translucent
 * circles) and the loop stops.
 *
 * Every ring starts at phase 0 (a tiny dot) — rings on the second layer are delayed rather than
 * phase-shifted, so none ever appears pre-grown (which would read as playing in reverse).
 *
 * Drives each layer's `circle-radius` and `circle-opacity` directly via setPaintProperty; all
 * map calls are guarded against a layer removed by a style reload.
 */
export function createHaloPulse(map: maplibregl.Map, opts: HaloPulseOptions): HaloPulse {
    let rafId: number | null = null;
    let startTs: number | null = null;

    const lifeMs = opts.cycleMs;
    // A new ring is born every spawnMs; overlapMs of each ring's life overlaps the next.
    const spawnMs = opts.cycleMs - opts.overlapMs;
    // The radar runs until the last ring is born; that ring then settles into the inner halo.
    const pulseEnd = (opts.pulses - 1) * spawnMs;

    const hidden: RingState = { radius: opts.minRadius, opacity: 0 };

    const toRadiusPaintValue = opts.toRadiusPaintValue ?? ((radius: number) => radius);

    const apply = (layerId: string, state: RingState) => {
        try {
            map.setPaintProperty(layerId, 'circle-radius', toRadiusPaintValue(state.radius));
            map.setPaintProperty(layerId, 'circle-opacity', state.opacity);
        } catch {
            // Layer may have been removed during a style reload — ignore.
        }
    };

    // A ring's radius/opacity at a point in its life (phase 0..1): born tiny and bright, fading out.
    const ringAt = (phase: number): RingState => ({
        radius: opts.minRadius + (opts.maxRadius - opts.minRadius) * easeOut(phase),
        opacity: opts.peakOpacity * (1 - phase),
    });

    // A halo layer renders alternate rings (even ring indices on layer 0, odd on layer 1). This
    // returns the state of the most recent ring on the given layer, or hidden if none is alive.
    const layerStateAt = (parity: 0 | 1, elapsed: number): RingState => {
        const recent = Math.floor(elapsed / spawnMs);
        const k = recent % 2 === parity ? recent : recent - 1;
        if (k < 0 || k > opts.pulses - 1) return hidden;
        const phase = (elapsed - k * spawnMs) / lifeMs;
        if (phase >= 1) return hidden;
        return ringAt(phase);
    };

    // The state each layer holds at the instant the radar ends and the settle begins. The last
    // ring (just born, tiny) is on one layer; the previous (large, fading) on the other.
    const innerFrom = layerStateAt(0, pulseEnd);
    const outerFrom = layerStateAt(1, pulseEnd);

    const settleRing = (from: RingState, settleRadius: number, settleOpacity: number, t: number): RingState => ({
        radius: lerp(from.radius, settleRadius, t),
        opacity: lerp(from.opacity, settleOpacity, t),
    });

    const frame = (ts: number) => {
        if (startTs == null) startTs = ts;
        const elapsed = ts - startTs;

        if (elapsed < pulseEnd) {
            apply(opts.innerLayerId, layerStateAt(0, elapsed));
            apply(opts.outerLayerId, layerStateAt(1, elapsed));
            rafId = requestAnimationFrame(frame);
            return;
        }

        // Settle: each layer tweens from its last radar state into its static resting halo, so the
        // pulse resolves into the two settled circles.
        const t = Math.min(1, (elapsed - pulseEnd) / opts.settleMs);
        apply(opts.innerLayerId, settleRing(innerFrom, opts.innerSettleRadius, opts.innerSettleOpacity, t));
        apply(opts.outerLayerId, settleRing(outerFrom, opts.outerSettleRadius, opts.outerSettleOpacity, t));

        if (t < 1) {
            rafId = requestAnimationFrame(frame);
        } else {
            // Land exactly on the static resting halos, then stop.
            apply(opts.innerLayerId, { radius: opts.innerSettleRadius, opacity: opts.innerSettleOpacity });
            apply(opts.outerLayerId, { radius: opts.outerSettleRadius, opacity: opts.outerSettleOpacity });
            rafId = null;
        }
    };

    return {
        play() {
            if (rafId != null) cancelAnimationFrame(rafId);
            startTs = null;
            // Paint the opening frame synchronously (before the next browser repaint) so a newly
            // selected POI never flashes the previous selection's settled halos for a frame while
            // waiting for the first requestAnimationFrame.
            apply(opts.innerLayerId, layerStateAt(0, 0));
            apply(opts.outerLayerId, layerStateAt(1, 0));
            rafId = requestAnimationFrame(frame);
        },
        stop() {
            if (rafId != null) cancelAnimationFrame(rafId);
            rafId = null;
        },
    };
}
